import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { getUserImages, formatImageSize, deleteImage, getSharedImages } from './utilities/imageUtils.js';
import UploadImage from './UploadImage.jsx';
import FixedMenu from '../Menus/FixedMenu.jsx';
import ConfirmationDialog from '../Feedback/ConfirmationDialog.jsx';
import FeedbackNotice from '../Feedback/FeedbackNotice.jsx';
import useConfirmation from '../Feedback/useConfirmation.js';
/**
 * ImageGallery Component - Image selection interface with Firebase integration
 * 
 * This component displays a gallery of images stored in Firebase Firestore.
 * It fetches base64-encoded images from the user's Firebase collection and allows
 * users to select an image to add to their blog content.
 * 
 * Features:
 *   - Fetches images from Firebase Firestore (users/{userId}/images)
 *   - Displays thumbnails in a responsive grid with metadata
 *   - Click to select and add image reference (imageId only) to the blog
 *   - Images are loaded on-demand from Firebase when edited
 *   - Delete images with confirmation dialog
 *   - Error handling for failed image loads
 *   - Upload new images directly within gallery
 *   - Smooth scroll to gallery on load
 *   - Image metadata display (filename, size)
 *   - Success/error messages for actions
 * 
 * Props:
 *   - userId: Current user's unique ID
 *   - setShowGallery: Function to toggle gallery visibility
 *   - addElement: Callback to add selected image reference to the blog
 */
const ImageGallery = ({ userId, setShowGallery, addElement, onSelectImage, requestConfirmation }) => {
    const [images, setImages] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const localConfirmation = useConfirmation();
    const askForConfirmation = requestConfirmation || localConfirmation.requestConfirmation;

    /**
     * Load images from Firebase
     * Fetches all base64-encoded images for the current user
     */
    const loadImages = useCallback(async () => {
        setLoading(true);
        setError(null);

        if (userId) {
            try {
                const userImages = await getUserImages(userId);
                setImages(userImages || []);

                if (userImages && userImages.length > 0) {
                    // Scroll to bottom after images load
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            } catch (err) {
                console.error('Error fetching images:', err);
                setError('Failed to load images from Firebase');
                setImages([]);
            } finally {
                setLoading(false);
            }
        }
        else {
            try {
                const sharedImages = await getSharedImages();
                setImages(sharedImages || []);

                if (sharedImages && sharedImages.length > 0) {
                    // Scroll to bottom after images load
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            } catch (err) {
                console.error('Error fetching images:', err);
                setError('Failed to load images from Firebase');
                setImages([]);
            } finally {
                setLoading(false);
            }

        }

    }, [userId]);

    /**
     * Load images on component mount or when userId changes
     */
    useEffect(() => {
        loadImages();
    }, [loadImages]);

    /**
     * Handle image selection from gallery
     * Creates an image item object with only imageId reference
     * The actual image will be loaded from Firebase on demand by EditImage
     */
    const chooseImage = (imageId) => {
        const selectedImage = images.find((image) => image.id === imageId) || { id: imageId };
        if (onSelectImage) {
            onSelectImage(selectedImage);
            setShowGallery(false);
            return;
        }
        const imageJSON = {
            type: 'image',
            imageId: imageId, // Store only reference - image loaded dynamically from Firebase
            'aspect-ratio': '16 / 9',
            paddingT: 'pt-1',
            paddingB: 'pb-1',
            paddingL: 'ps-1',
            paddingR: 'pe-1',
        };

        addElement(imageJSON);
        setShowGallery(false); // Close the gallery after selection
    };

    /**
     * Handle successful upload
     * Refreshes the gallery to show newly uploaded image
     */
    const handleUploadSuccess = () => {
        loadImages();
        setShowUploadForm(false);
    };

    /**
     * Handle image deletion with confirmation
     * Deletes the image from Firebase and refreshes the gallery
     */
    const deleteSelectedImage = async (imageId, filename) => {
        setDeletingId(imageId);
        setDeleteMessage(null);

        try {
            const result = await deleteImage(userId, imageId);

            if (result.success) {
                setDeleteMessage({
                    type: 'success',
                    text: `Image "${filename}" deleted successfully`,
                });
                // Refresh the gallery
                await loadImages();
            } else {
                setDeleteMessage({
                    type: 'error',
                    text: result.error || 'Failed to delete image',
                });
            }
        } catch (err) {
            console.error('Error deleting image:', err);
            setDeleteMessage({
                type: 'error',
                text: `Error deleting image: ${err.message}`,
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handleDeleteImage = (imageId, filename) => {
        askForConfirmation({
            title: `Delete "${filename}"?`,
            message: 'This image will be permanently removed from your image library.',
            confirmLabel: 'Delete image',
            busyLabel: 'Deleting...',
            variant: 'danger',
            onConfirm: () => deleteSelectedImage(imageId, filename),
        });
    };

    return (
        <FixedMenu
            title="Image Gallery"
            iconClass="bi bi-image"
            expanded={true}
            setExpanded={setShowGallery}
>
                

                <div className="">
                    {/* Delete message */}
                    <FeedbackNotice
                        message={deleteMessage?.text || ''}
                        variant={deleteMessage?.type === 'success' ? 'success' : 'danger'}
                        onDismiss={() => setDeleteMessage(null)}
                    />

                    {/* Upload form */}
                    {showUploadForm && (
                        <div className="mb-4 p-3 border rounded bg-light">
                            <UploadImage userId={userId} onUploadSuccess={handleUploadSuccess} />
                        </div>
                    )}

                    {!showUploadForm && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setShowUploadForm(true)}
                                >
                                    <i className="bi bi-cloud-upload"></i> Upload New Image
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Loading state */}
                    {loading && (
                        <div className="text-center">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    {/* Error state */}
                    {error && !loading && (
                        <div className="alert alert-danger mb-0">{error}</div>
                    )}

                    {/* Gallery display */}
                    {!loading && !error && images.length === 0 && (
                        <p className="text-muted">No images found. Upload your first image to get started.</p>
                    )}

                    {!loading && !error && images.length > 0 && (
                        <div>
                            <p className="text-muted mb-3">
                                <small>
                                    {images.length} image{images.length !== 1 ? 's' : ''} available
                                </small>
                            </p>
                            <div className="row">
                                {images.map((image) => (
                                    <div
                                        key={image.id}
                                        className="col-12 col-sm-6 col-md-4 mb-3"
                                    >
                                        <div
                                            className="position-relative overflow-hidden rounded"
                                            style={{ paddingBottom: '75%', cursor: 'pointer' }}
                                            onClick={() => chooseImage(image.id)}
                                        >
                                            <img
                                                src={image.base64}
                                                alt={image.filename}
                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                style={{ objectFit: 'cover' }}
                                            />
                                            {/* Delete button overlay */}
                                            <button
                                                className="btn btn-sm btn-danger"
                                                style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    opacity: 0.9,
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteImage(image.id, image.filename);
                                                }}
                                                disabled={deletingId === image.id}
                                                title="Delete image"
                                            >
                                                {deletingId === image.id ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                ) : (
                                                    <i className="bi bi-trash"></i>
                                                )}
                                            </button>
                                        </div>
                                        <small className="text-muted d-block mt-1 text-truncate">
                                            {image.filename} ({formatImageSize(image.size)})
                                        </small>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {!requestConfirmation ? <ConfirmationDialog {...localConfirmation.confirmationProps} /> : null}
        </FixedMenu>
    );
};

ImageGallery.propTypes = {
    userId: PropTypes.string.isRequired,
    setShowGallery: PropTypes.func.isRequired,
    addElement: PropTypes.func,
    onSelectImage: PropTypes.func,
    requestConfirmation: PropTypes.func,
};

export default ImageGallery;
